import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { SponsorPublic, SponsorUserPublic, SponsorLeadConfigPublic, LeadSubmissionPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { safeHref } from "@/lib/safe-url";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pencil, Trash2, Upload, UserPlus, Zap, Image } from "lucide-react";
import { LeadSubmissionsTable } from "@/components/lead-submissions-table";
import { Badge } from "@/components/ui/badge";
import { PinButton } from "@/components/pin-button";
import { ListBackButton } from "@/components/list-back-button";

export default function SponsorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState<SponsorPublic | null>(null);
  const [users, setUsers] = useState<SponsorUserPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRoleType, setNewRoleType] = useState<"sponsor_admin" | "sponsor_user">("sponsor_user");
  const [addingUser, setAddingUser] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Image upload state
  const [uploading, setUploading] = useState(false);

  // Lead config state (067)
  const [leadConfigs, setLeadConfigs] = useState<SponsorLeadConfigPublic[]>([]);
  const [leads, setLeads] = useState<LeadSubmissionPublic[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [uploadingLogo, setUploadingLogo] = useState<string | null>(null);

  async function handleLogoUpload(configId: string, file: File) {
    if (!id) return;
    setUploadingLogo(configId);
    try {
      const res = await api.uploadLeadConfigLogo(id, configId, file);
      setLeadConfigs((prev) => prev.map((c) => (c.id === configId ? res.config : c)));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to upload logo");
    } finally {
      setUploadingLogo(null);
    }
  }

  async function handleDeleteLogo(configId: string) {
    if (!id || !confirm("Remove this logo?")) return;
    try {
      const res = await api.deleteLeadConfigLogo(id, configId);
      setLeadConfigs((prev) => prev.map((c) => (c.id === configId ? res.config : c)));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to delete logo");
    }
  }

  function refreshLeads() {
    if (!id) return;
    api.getSponsorLeads(id).then((res) => {
      setLeads(res.leads);
      setLeadsTotal(res.total);
    }).catch(() => {});
  }

  async function handleImageUpload(file: File) {
    if (!id) return;
    setUploading(true);
    setError(null);
    try {
      const res = await api.uploadSponsorImage(id, file);
      setSponsor(res.sponsor);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to upload image");
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteImage() {
    if (!id || !confirm("Remove this brand image?")) return;
    try {
      const res = await api.deleteSponsorImage(id);
      setSponsor(res.sponsor);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to delete image");
    }
  }

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.getSponsor(id),
      api.getSponsorUsers(id),
      api.getSponsorLeadConfigs(id).catch(() => ({ configs: [] })),
      api.getSponsorLeads(id).catch(() => ({ leads: [], total: 0 })),
    ])
      .then(([sponsorRes, usersRes, configsRes, leadsRes]) => {
        setSponsor(sponsorRes.sponsor);
        setUsers(usersRes.users);
        setLeadConfigs(configsRes.configs);
        setLeads(leadsRes.leads);
        setLeadsTotal(leadsRes.total);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load sponsor"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleAddUser() {
    if (!id) return;
    setAddError(null);
    setAddingUser(true);

    try {
      await api.addSponsorUser(id, { email: newEmail, role_type: newRoleType });
      // Refresh user list
      const usersRes = await api.getSponsorUsers(id);
      setUsers(usersRes.users);
      setNewEmail("");
      setShowAddUser(false);
    } catch (err) {
      setAddError(err instanceof api.ApiError ? err.message : "Failed to add user");
    } finally {
      setAddingUser(false);
    }
  }

  async function handleRemoveUser(userId: string) {
    if (!id) return;
    try {
      await api.removeSponsorUser(id, userId);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to remove user");
    }
  }

  async function handleDelete() {
    if (!id || !confirm("Delete this sponsor? This action cannot be undone.")) return;
    try {
      await api.deleteSponsor(id);
      navigate("/sponsors");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete sponsor");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="sponsor-detail" data-state="loading">Loading...</p>;
  }

  if (error && !sponsor) {
    return <p className="text-sm text-destructive" data-page="sponsor-detail" data-state="error" data-error>{error}</p>;
  }

  if (!sponsor) {
    return <p className="text-muted-foreground" data-page="sponsor-detail" data-state="empty">Sponsor not found</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="sponsor-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          <ListBackButton to="/sponsors" label="Sponsors" />
          <h1 className="text-2xl font-semibold">{sponsor.name}</h1>
          <p className="text-muted-foreground">{sponsor.contact_name}</p>
        </div>
        <div className="flex gap-2">
          <PinButton pinType="sponsor" entityType="Sponsor" entityId={sponsor.id} />
          {sponsor._actions.includes("edit") && (
            <Link
              to={`/sponsors/${sponsor.slug}/edit`}
              className={buttonVariants({ variant: "outline" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          )}
          {sponsor._actions.includes("delete") && (
            <Button variant="destructive" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <Card data-region="info">
        <CardHeader>
          <CardTitle className="text-base">Sponsor Information</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="mt-1">{sponsor.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Full Company Name</dt>
              <dd className="mt-1">{sponsor.full_name ?? "--"}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Website</dt>
              <dd className="mt-1">
                {sponsor.website_url ? (
                  safeHref(sponsor.website_url) ? (
                    <a href={safeHref(sponsor.website_url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {sponsor.website_url}
                    </a>
                  ) : (
                    <span>{sponsor.website_url}</span>
                  )
                ) : "--"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Industry</dt>
              <dd className="mt-1">{sponsor.industry ?? "--"}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Contact Name</dt>
              <dd className="mt-1">{sponsor.contact_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Contact Email</dt>
              <dd className="mt-1">{sponsor.contact_email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Contact Phone</dt>
              <dd className="mt-1">{sponsor.contact_phone ?? "--"}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1">
                {new Date(sponsor.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {(sponsor._actions.includes("edit") || sponsor._actions.includes("manage_users")) && (
        <Card data-region="brand-image">
          <CardHeader>
            <CardTitle className="text-base">Brand Image</CardTitle>
          </CardHeader>
          <CardContent>
            {sponsor.image_url ? (
              <div className="flex flex-col gap-4">
                <img
                  src={sponsor.image_url}
                  alt={`${sponsor.name} brand`}
                  className="max-h-48 rounded-md border border-border object-contain"
                />
                <Button variant="destructive" size="sm" className="w-fit" onClick={handleDeleteImage}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove Image
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="sponsor-image"
                  className="flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-border p-6 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  <Upload className="h-8 w-8" />
                  <span className="text-sm font-medium">
                    {uploading ? "Uploading..." : "Click to upload brand image"}
                  </span>
                  <span className="text-xs">JPEG, PNG, WebP, or SVG. Max 5MB.</span>
                </label>
                <input
                  id="sponsor-image"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lead Generation (067) */}
      {(sponsor._actions.includes("edit") || sponsor._actions.includes("manage_users")) && (
        <>
          <Card data-region="lead-configs">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Lead Generation
              </CardTitle>
              {leadConfigs.length > 0 && (
                <Badge variant="secondary">{leadConfigs.length} config{leadConfigs.length !== 1 ? "s" : ""}</Badge>
              )}
            </CardHeader>
            <CardContent>
              {leadConfigs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No lead generation configs. Contact admin to set up.</p>
              ) : (
                <div className="space-y-4">
                  {leadConfigs.map((config) => (
                    <div key={config.id} className="rounded-md border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{config.provider_name}</span>
                          <Badge variant={config.active ? "default" : "secondary"}>
                            {config.active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">{config.provider_type}</span>
                      </div>

                      <dl className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <dt className="text-muted-foreground">CTA</dt>
                          <dd>{config.cta_label}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Tagline</dt>
                          <dd>{config.tagline ?? "--"}</dd>
                        </div>
                      </dl>

                      {/* Logo upload */}
                      <div className="border-t border-border pt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Image className="h-3 w-3" /> Logo
                        </p>
                        {config.logo_url ? (
                          <div className="flex items-center gap-3">
                            <img
                              src={config.logo_url}
                              alt={`${config.provider_name} logo`}
                              className="h-10 max-w-[120px] object-contain rounded border border-border"
                            />
                            <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => handleDeleteLogo(config.id)}>
                              <Trash2 className="h-3 w-3 mr-1" /> Remove
                            </Button>
                          </div>
                        ) : (
                          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors w-fit">
                            <Upload className="h-3 w-3" />
                            {uploadingLogo === config.id ? "Uploading..." : "Upload logo"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/svg+xml"
                              className="hidden"
                              disabled={uploadingLogo === config.id}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload(config.id, file);
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lead Submissions */}
          <Card data-region="lead-submissions">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Lead Submissions</CardTitle>
              <Badge variant="secondary">{leadsTotal} total</Badge>
            </CardHeader>
            <CardContent>
              <LeadSubmissionsTable leads={leads} total={leadsTotal} contextColumn="school" />
            </CardContent>
          </Card>
        </>
      )}

      {sponsor._actions.includes("manage_users") && (
        <Card data-region="users">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Users</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddUser(!showAddUser)}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </CardHeader>
          <CardContent>
            {showAddUser && (
              <div className="mb-4 flex flex-col gap-3 rounded-md border border-border p-4">
                {addError && <p className="text-sm text-destructive">{addError}</p>}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="userEmail">User Email</Label>
                  <Input
                    id="userEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="roleType">Role</Label>
                  <select
                    id="roleType"
                    aria-label="Role"
                    value={newRoleType}
                    onChange={(e) => setNewRoleType(e.target.value as "sponsor_admin" | "sponsor_user")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="sponsor_user">Sponsor User (read-only)</option>
                    <option value="sponsor_admin">Sponsor Admin (can manage users)</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={addingUser || !newEmail} onClick={handleAddUser}>
                    {addingUser ? "Adding..." : "Add"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddUser(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {users.length === 0 && !showAddUser && (
              <p className="text-sm text-muted-foreground">No users linked to this sponsor</p>
            )}

            {users.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="w-16" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.role_type === "sponsor_admin" ? "Admin" : "User"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUser(user.user_id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
